import { Button, Col, Form, InputNumber, Row, Select } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../../../app/hooks';
import { setModal } from '../../../../common/slices/commonSlice';
import { ISetState } from '../../../../common/types/commonTypes';
import {
  DateInput,
  FormInputItem,
  TextAreaInput,
} from '../../../../components/common/FormItem/FormItems';
import SelectPaymentMethod from '../../../../components/common/paymentMethod/SelectPaymentMethod';
import {
  IAccountCompanies,
  IAccountNonInvoiceIncome,
  IAccountNonInvoiceIncomeEditFormData,
  IaccountsByMethod,
} from '../../AccountsTypes/AccountsTypes';
import {
  useGetAllCompaniesQuery,
  useLazyGetAllAccountsByTypeQuery,
} from '../../Api/Endpoints/accountsEndpoints';
import { useEditNonInvoiceIncomeMutation } from '../../Api/Endpoints/nonInvoiceIncomeEndpoints';
import { loadingIndicator } from '../../../../components/common/spinner/LoadingIndicator';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../../auth/states/userSlice';

interface IProps {
  info: IAccountNonInvoiceIncome;
  setNull: ISetState<IAccountNonInvoiceIncome | null>;
}

function Non_Invoice_Income_Edit_Modal({ info, setNull }: IProps) {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const { data: companies } = useGetAllCompaniesQuery();
  const [paymentMethod, setPaymentMethod] = useState<string>();
  const isChanged = useRef(false);
  const user = useSelector(selectUser);

  useEffect(() => {
    form.setFieldsValue({
      company_id: info.nonincome_company_id,
      type_id: info.account_acctype_id,
      account_id: info.account_id,
      amount: info.actransaction_amount,
      date: dayjs(info.nonincome_created_date),
    });
    if (info.nonincome_cheque_no) {
      form.setFieldValue('cheque_no', info.nonincome_cheque_no);
    }
    if (info.nonincome_receipt_no) {
      form.setFieldValue('receipt_no', info.nonincome_receipt_no);
    }
    if (info.account_acctype_id) {
      setPaymentMethod(info.account_acctype_id.toString());
    }
  }, [info]);

  const [fetchAccountsByPayment, { data: accountsByPayment }] =
    useLazyGetAllAccountsByTypeQuery();

  useEffect(() => {
    if (paymentMethod) {
      fetchAccountsByPayment(Number(paymentMethod));
    }

    if (isChanged.current) {
      form.setFieldsValue({ account_id: undefined });

      return;
    }
  }, [paymentMethod]);

  const [editNonInvoiceIncome, { isError, isLoading }] =
    useEditNonInvoiceIncomeMutation();

  const handleSubmit = async (values: IAccountNonInvoiceIncomeEditFormData) => {
    const body: IAccountNonInvoiceIncomeEditFormData = {
      ...values,
      nonincome_id: info.nonincome_id,
      date: dayjs(values.date).format('YYYY-MM-DD'),
      account_id: values.account_id,
      noninvoice_created_by: user?.user_id as number,
    };

    await editNonInvoiceIncome(body);
    setNull(null);
    dispatch(setModal(false));
    form.resetFields();
  };
  //loading indicator
  if (isLoading) {
    return loadingIndicator;
  }
  return (
    <Form layout='vertical' form={form} onFinish={handleSubmit}>
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 24 }}>
        <Col span={6} xs={24} sm={24} md={24} lg={12}>
          <Form.Item
            name='company_id'
            label='Company'
            rules={[
              {
                required: true,
                message: `Company name is required!`,
              },
            ]}
          >
            <Select
              showSearch
              allowClear
              placeholder={'Select Company'}
              optionFilterProp='children'
            >
              {companies?.data?.map((item: IAccountCompanies, index) => {
                return (
                  <Select.Option key={index} value={item.company_id}>
                    {`${item.company_name} [${item.company_id}]`}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        </Col>

        <SelectPaymentMethod
          name='type_id'
          label='Payment Method'
          size={12}
          onChange={(value: string) => {
            setPaymentMethod(value);
            if (!isChanged.current) isChanged.current = true;
          }}
        />
        {Number(paymentMethod) !== 4 && (
          <Col span={6} xs={24} sm={24} md={24} lg={12}>
            <Form.Item
              name='account_id'
              label='Account'
              rules={[
                {
                  required: true,
                  message: `Account is required!`,
                },
              ]}
            >
              <Select
                showSearch
                allowClear
                placeholder={'Select Account'}
                optionFilterProp='children'
              >
                {accountsByPayment?.data?.map(
                  (item: IaccountsByMethod, index) => {
                    return (
                      <Select.Option key={index} value={item.account_id}>
                        {`${item.account_name}`}
                      </Select.Option>
                    );
                  }
                )}
              </Select>
            </Form.Item>
          </Col>
        )}

        {Number(paymentMethod) == 4 && (
          <>
            <FormInputItem name='cheque_no' label='Cheque No.' size={12} />
            <FormInputItem
              name='receipt_no'
              label='Receipt/Trans No.'
              size={12}
            />
          </>
        )}
        <Col span={6} xs={24} sm={24} md={24} lg={12}>
          <Form.Item name='amount' label='Amount' required>
            <InputNumber required style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <DateInput required name='date' label='Date' size={12} />
        <TextAreaInput name='note' label='Note' size={24} />
      </Row>

      <Form.Item>
        <Button htmlType='submit' type='primary' loading={isLoading}>
          Edit Non-Invoice Income
        </Button>
      </Form.Item>
    </Form>
  );
}

export default Non_Invoice_Income_Edit_Modal;
